import type {SceneBoundaries} from "../Sources";
import {Vector3} from "three";
import type {SidebarValues} from "../App.tsx";
import type {TouchingBoundary} from "../Scene.tsx";

/**
 * Controller for position-related logic.
 *
 * Handles position calculations, clamping, boundary detection, and visual feedback.
 */
export class PositionController {

    private readonly bounds: SceneBoundaries;
    private readonly panHorizontal: boolean;
    private readonly panVertical: boolean;

    private readonly setValues: SidebarValues;
    private clamped: Record<"x" | "y" | "z", boolean> = { x: false, y: false, z: false };
    private speed : { x: number; y: number; z: number } = { x: 0, y: 0, z: 0 };

    constructor(bounds: SceneBoundaries,
                panning : { horizontal: boolean, vertical: boolean },
                setGestureValues: SidebarValues) {
        this.bounds = bounds;
        this.panHorizontal = panning.horizontal;
        this.panVertical = panning.vertical;
        this.setValues = setGestureValues;
    }

    calculatePosition(position: Vector3, delta: { dx?: number; dy?: number; dz?: number })
        : {changed: boolean, newPosition: { x: number; y: number; z: number },
            atBoundary: TouchingBoundary } {

        const newPosition = {...position};
        const scaleXY = 0.003 - newPosition.z * 0.00016;

        if (delta.dx) {
            let x = position.x + delta.dx * scaleXY;
            this.speed.x = (x - position.x) / this.bounds.maxX * 2;
            x = this.clamp(-this.bounds.maxX, this.bounds.maxX, x);
            this.clamped.x = (x === -this.bounds.maxX || x === this.bounds.maxX);
            if (this.panHorizontal) {
                newPosition.x = x;
            }
        } else {
            this.speed.x = 0;
        }

        if (delta.dy) {
            let y = position.y + delta.dy * scaleXY;
            this.speed.y = (y - position.y) / this.bounds.maxY * 2;
            y = this.clamp(-this.bounds.maxY, this.bounds.maxY, y);
            this.clamped.y = (y === -this.bounds.maxY || y === this.bounds.maxY);
            if (this.panVertical) {
                newPosition.y = y;
            }
        } else {
            this.speed.y = 0;
        }

        if (delta.dz) {
            let z = position.z + delta.dz * 0.01;
            this.speed.z = (z - position.z) / -this.bounds.minZ;
            z = this.clamp(this.bounds.minZ, 0, z);
            this.clamped.z = (z === this.bounds.minZ || z === 0);
            newPosition.z = z;
        } else {
            this.speed.z = 0;
        }

        const direction = this.getMainDirection();
        const velocity = direction ? this.speed[direction as keyof typeof this.speed] * 1000 : undefined;
        this.setValues(prev => ({
            ...prev,
            "direction": direction ? velocity!.toFixed(0) + " " + direction : "",
        }));

        return {
            changed: direction !== undefined && (direction === "x" && newPosition.x !== position.x
                || direction === "y" && newPosition.y !== position.y
                || direction === "z" && newPosition.z !== position.z),
            newPosition : newPosition,
            atBoundary: {
                back: newPosition.z === this.bounds.minZ && direction === "z",
                left: newPosition.x === - this.bounds.maxX && direction === "x",
                right: newPosition.x === this.bounds.maxX && direction === "x",
                top: newPosition.y === this.bounds.maxY && direction === "y",
                bottom: newPosition.y === - this.bounds.maxY && direction === "y",
                yPinned: !this.panVertical && direction === "y" && newPosition.y === position.y ? newPosition.y : null
            }
        }
    }

    private clamp(min: number, max: number, value: number): number {
        return Math.min(Math.max(value, min), max);
    }

    hitBoundary() : boolean {
        const direction = this.getMainDirection();
        return this.clamped.x && direction === "x" || this.clamped.y && direction === "y"
            || this.clamped.z && direction === "z";
    }

    highSpeed() {
        const direction = this.getMainDirection();
        return direction && Math.abs(this.speed[direction as keyof typeof this.speed]) > 0.03;
    }

    private getMainDirection() : "x" | "y" | "z" | undefined {
        const absSpeed = {
            x: Math.abs(this.speed.x),
            y: Math.abs(this.speed.y),
            z: Math.abs(this.speed.z)
        };
        //console.log("speed: " + absSpeed.x.toFixed(3) + ", " + absSpeed.y.toFixed(3) + ", " + absSpeed.z.toFixed(3));
        if (absSpeed.x < 0.0001 && absSpeed.y < 0.0001 && absSpeed.z < 0.0001) return undefined;
        if (absSpeed.x > absSpeed.y && absSpeed.x > absSpeed.z) return "x";
        if (absSpeed.y > absSpeed.x && absSpeed.y > absSpeed.z) return "y";
        if (absSpeed.z > absSpeed.x && absSpeed.z > absSpeed.y) return "z";
        return undefined;
    }


}

